import React, { useRef, useState } from 'react';
import { Flame, Users, Zap, X, Download, Share2, Loader2, Waves } from 'lucide-react';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';

const RANK_TITLES = {
  1: 'Stoke King',
  2: 'Wave Whisperer',
  3: 'Ripple Maker',
};

function getRankTitle(rank, aura) {
  if (RANK_TITLES[rank]) return RANK_TITLES[rank];
  if (aura >= 100) return 'Stoke Spreader';
  if (aura >= 50) return 'Crew Builder';
  return 'Stoke Starter';
}

export default function ReferralCard({ user, rank, stats, onClose }) {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  const aura = stats?.total_referral_aura || 0;
  const invites = stats?.direct_invites || 0;
  const ripples = stats?.referral_events || 0;
  const title = getRankTitle(rank, aura);
  const referralCode = user?.referral_code || '';
  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const captureCard = async () => {
    if (!cardRef.current || generating) return null;
    setGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
      });
      return canvas;
    } catch {
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    const canvas = await captureCard();
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `surfswell-rank-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleShare = async () => {
    const canvas = await captureCard();
    if (!canvas) return;
    try {
      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
      if (navigator.canShare && blob) {
        const file = new File([blob], 'surfswell-rank.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `#${rank} ${title} on SurfSwell.ai`,
            text: `I'm ranked #${rank} with ${aura} referral aura on SurfSwell.ai — spreading the stoke!`,
            files: [file],
          });
          return;
        }
      }
      if (blob) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        alert('Copied to clipboard!');
      }
    } catch (e) {
      if (e.name !== 'AbortError') handleDownload();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="referral-card-overlay"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="relative z-10 w-full max-w-sm"
      >
        <button
          data-testid="referral-card-close-btn"
          onClick={onClose}
          className="absolute -top-3 -right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity shadow-lg"
          style={{ backgroundColor: '#000', border: '2px solid rgba(255,255,255,0.2)' }}
        >
          <X className="w-4 h-4" style={{ color: '#fff' }} />
        </button>

        {/* ====== THE CARD (captured by html2canvas) ====== */}
        <div
          ref={cardRef}
          className="rounded-3xl overflow-hidden shadow-2xl"
          style={{ background: 'linear-gradient(135deg, #1a0a00 0%, #2d1400 20%, #4a2000 45%, #1a0a00 70%, #0d0500 100%)' }}
          data-testid="referral-share-card"
        >
          <div className="relative" style={{ minHeight: 420 }}>
            {/* Subtle texture overlay */}
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(ellipse at 30% 20%, rgba(251,191,36,0.08) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(245,158,11,0.05) 0%, transparent 50%)',
            }} />

            <div className="relative z-10 p-6 flex flex-col" style={{ minHeight: 420 }}>
              {/* Top — title badge */}
              <div className="flex items-center justify-between mb-auto">
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full"
                  style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24' }}
                >
                  {title}
                </span>
                <span className="text-xs font-medium" style={{ color: 'rgba(251,191,36,0.5)' }}>
                  {dateStr}
                </span>
              </div>

              {/* Center — rank hero */}
              <div className="my-auto text-center py-4">
                {/* Avatar */}
                {user?.picture ? (
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 mx-auto mb-3" style={{ borderColor: 'rgba(251,191,36,0.3)' }}>
                    <img src={user.picture} alt="" crossOrigin="anonymous" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center text-xl font-bold" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '2px solid rgba(251,191,36,0.3)' }}>
                    {(user?.name || '?')[0].toUpperCase()}
                  </div>
                )}

                {/* Rank number */}
                <p className="font-bold leading-none" style={{ fontSize: 64, color: '#fbbf24', textShadow: '0 2px 20px rgba(251,191,36,0.3)' }}>
                  #{rank}
                </p>
                <p className="text-base font-semibold mt-1" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  {user?.name?.split(' ')[0] || 'Surfer'}
                </p>
                <p className="text-sm mt-1 italic" style={{ color: 'rgba(251,191,36,0.6)' }}>
                  spreading the stoke
                </p>
              </div>

              {/* Bottom — stats strip + branding */}
              <div className="mt-auto">
                {/* Stats strip */}
                <div
                  className="grid grid-cols-3 gap-2 rounded-2xl p-3 mb-4"
                  style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.1)' }}
                >
                  <div className="text-center">
                    <Flame className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: 'rgba(251,191,36,0.6)' }} />
                    <p className="text-sm font-bold" style={{ color: '#fbbf24' }}>+{aura}</p>
                    <p className="text-[9px]" style={{ color: 'rgba(251,191,36,0.4)' }}>aura</p>
                  </div>
                  <div className="text-center">
                    <Users className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: 'rgba(251,191,36,0.6)' }} />
                    <p className="text-sm font-bold" style={{ color: '#fbbf24' }}>{invites}</p>
                    <p className="text-[9px]" style={{ color: 'rgba(251,191,36,0.4)' }}>invites</p>
                  </div>
                  <div className="text-center">
                    <Zap className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color: 'rgba(251,191,36,0.6)' }} />
                    <p className="text-sm font-bold" style={{ color: '#fbbf24' }}>{ripples}</p>
                    <p className="text-[9px]" style={{ color: 'rgba(251,191,36,0.4)' }}>ripples</p>
                  </div>
                </div>

                {/* Branding strip */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Waves className="w-4 h-4" style={{ color: '#fbbf24' }} />
                    <span
                      className="text-xs font-bold tracking-wide"
                      style={{ color: '#fbbf24' }}
                      data-testid="referral-card-brand"
                    >
                      SurfSwell.ai{referralCode ? `/r/${referralCode}` : ''}
                    </span>
                  </div>
                  <span className="text-[10px]" style={{ color: 'rgba(251,191,36,0.3)' }}>
                    Stoke Spreader
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ====== ACTION BUTTONS ====== */}
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            data-testid="referral-card-share-btn"
            onClick={handleShare}
            disabled={generating}
            className="flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-2xl hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ background: '#fbbf24', color: '#1a0a00' }}
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
          <button
            data-testid="referral-card-download-btn"
            onClick={handleDownload}
            disabled={generating}
            className="flex items-center gap-2 text-sm font-medium px-6 py-3 rounded-2xl hover:opacity-80 transition-opacity shadow-md disabled:opacity-50"
            style={{ backgroundColor: '#000', color: '#fff' }}
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Save
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
